import { useEffect, useState } from 'react';
import { useInfiniteQuery, useQueryClient, useQuery } from '@tanstack/react-query';
import { salesStreamApi } from '../api';

const fetchSalesStream = async (
  limit,
  offset = 0,
  filter = [],
  sortingValue = 'datetime',
  cotaAtFloorPercentage = null
) => {
  const params = new URLSearchParams({
    limit,
    offset,
    event_sorting: sortingValue,
  });

  if (filter.length > 0) {
    const filterValues = filter.reduce((acc, currentFilter) => {
      const [type, value] = currentFilter.split('|');
      if (acc[type]) {
        acc[type].push(value);
      } else {
        acc[type] = [value];
      }
      return acc;
    }, {});

    Object.entries(filterValues).forEach(([filterName, valuesArray]) => {
      params.append(filterName, valuesArray.join(','));
    });
  }

  if (cotaAtFloorPercentage !== null) {
    params.append('percentage_cota_at_floor_min', cotaAtFloorPercentage);
  }

  const url = `v1/event/catalog?${params.toString()}`;

  const response = await salesStreamApi.get(url);
  return response;
};

const fetchSalesStreamFilters = async () => {
  const response = await salesStreamApi.get(`v1/filters`);
  return response;
};

const fetchSalesStreamSummary = async (filter = []) => {
  let url = 'v1/summary/stats?';
  if (filter && filter.length > 0) {
    const filterValues = {};
    filter.forEach(option => {
      const [type, value] = option.split('|');
      filterValues[type] = filterValues[type] ? filterValues[type] + (value ? ',' : '') + value : value;
    });
    Object.keys(filterValues).forEach((key, index) => {
      if (index < key.length - 1 && index !== 0) {
        url += '&';
      }
      url += `${key}=${filterValues[key]}`;
    });
  }
  const response = await salesStreamApi.get(url);
  return response;
};

export const fetchSalesStreamTargetHeld = async events => {
  const response = await salesStreamApi.get(`v1/target/${events}`);
  return response;
};

export const useInfiniteQuerySalesStream = (
  limit = 15,
  filter = [],
  sortingValue = 'datetime',
  cotaAtFloorPercentage = null
) => {
  const queryClient = useQueryClient();
  const [displayedItems, setDisplayedItems] = useState([]); // State to track displayed items

  const resetInfiniteQueryPagination = () => {
    queryClient.clear();
  };

  useEffect(() => {
    queryClient.resetQueries({
      queryKey: ['/sales-stream'],
      exact: true,
    });
  }, [queryClient]);

  const { data, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } = useInfiniteQuery({
    queryKey: ['/sales-stream', filter.join(), cotaAtFloorPercentage],
    queryFn: ({ pageParam = 0 }) => {
      const offset = pageParam || 0;
      return fetchSalesStream(limit, offset, filter, sortingValue, cotaAtFloorPercentage);
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.data?.length, 0);
      if (totalFetched < lastPage.total) {
        return totalFetched; // Next offset is the current total fetched
      }
      return undefined; // No more pages to load
    },
    cacheTime: 1000 * 60 * 2, // Cache time: 2 minutes
    staleTime: 1000 * 60 * 5, // Stale time: 5 minutes
    refetchOnMount: true,
    refetchInterval: 1000 * 60 * 5, // Refetch interval: 5 minutes
    refetchOnWindowFocus: true,
    onSuccess: response => {
      const allItems = response.pages.flatMap(page => page.data);
      setDisplayedItems(allItems); // Set displayed items
    },
  });

  return {
    data,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    displayedItems,
    resetInfiniteQueryPagination,
  };
};

export const useQuerySalesStreamFilters = () => {
  const { data, isFetching, status, error, refetch } = useQuery({
    queryKey: [`/sales-stream/filters`],
    queryFn: () => fetchSalesStreamFilters(),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
  return {
    data,
    isFetching,
    status,
    error,
    refetch,
  };
};

export const useQuerySalesStreamSummary = filter => {
  const { data, isFetching, status, error, refetch } = useQuery({
    queryKey: [`/sales-stream/summary/${filter}`],
    queryFn: () => fetchSalesStreamSummary(filter),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    // Make it stale so if same filter is consulted after change it will refetch data again
    staleTime: 0,
  });
  return {
    data,
    isFetching,
    status,
    error,
    refetch,
  };
};
